import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listUsers, repairUser, updateUserRoles, updateUserStatus, setTeacherVerificationStatus, listAuditLogs, AdminUser } from '../services/admin';
import { listAdminPendingRequests, resolvePermissionRequest } from '../services/permissionRequests';
import { UserRole, PermissionRequest, VerificationStatus, AuditLogEntry } from '../types/models';

const ALL_ROLES: UserRole[] = ['Yogi', 'Organizer', 'Teacher', 'Admin'];

const ROLE_COLORS: Record<UserRole, string> = {
  Admin:     'bg-red-100 text-red-700',
  Teacher:   'bg-purple-100 text-purple-700',
  Organizer: 'bg-blue-100 text-blue-700',
  Yogi:      'bg-green-100 text-green-700',
};

const VERIFICATION_COLORS: Record<VerificationStatus, string> = {
  verified:    'bg-green-100 text-green-700',
  provisional: 'bg-yellow-100 text-yellow-700',
  unverified:  'bg-gray-100 text-gray-500',
  rejected:    'bg-red-100 text-red-700',
};

function auditSummary(entry: AuditLogEntry): string {
  const d = entry.details;
  switch (entry.actionType) {
    case 'permission_request.approved': return `Approved ${d.targetRole} role`;
    case 'permission_request.denied':   return `Denied ${d.targetRole} role`;
    case 'teacher.verification_updated': return `Verification: ${d.previousStatus} → ${d.newStatus}`;
    case 'user.roles_updated':          return `Roles: ${(d.previousRoles as string[]).join(',')} → ${(d.newRoles as string[]).join(',')}`;
    case 'user.status_updated':         return `Status: ${d.previousStatus} → ${d.newStatus}`;
    default:                            return entry.actionType;
  }
}

export const AdminPage: React.FC = () => {
  const { currentUser, isAuthenticated, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'audit'>('users');

  // Users tab state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [editingRoles, setEditingRoles] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [repairing, setRepairing] = useState<string | null>(null);
  const [permRequests, setPermRequests] = useState<PermissionRequest[]>([]);
  const [resolvingReq, setResolvingReq] = useState<string | null>(null);
  const [verifyingUser, setVerifyingUser] = useState<string | null>(null);

  // Audit log tab state
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditNextBefore, setAuditNextBefore] = useState<string | undefined>();
  const [auditFilterUserId, setAuditFilterUserId] = useState<string>('');
  const [auditFilterUserName, setAuditFilterUserName] = useState<string>('');
  const [auditUserSearch, setAuditUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, reqs] = await Promise.all([listUsers(), listAdminPendingRequests()]);
      setUsers(data);
      setPermRequests(reqs);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAuditLogs = useCallback(async (targetUserId?: string) => {
    try {
      setAuditLoading(true);
      setAuditError(null);
      const result = await listAuditLogs({ targetUserId: targetUserId || undefined });
      setAuditLogs(result.logs);
      setAuditNextBefore(result.nextBefore);
    } catch (err) {
      setAuditError((err as Error).message);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadMoreAuditLogs = async () => {
    if (!auditNextBefore) return;
    try {
      setAuditLoading(true);
      const result = await listAuditLogs({
        before: auditNextBefore,
        targetUserId: auditFilterUserId || undefined,
      });
      setAuditLogs(prev => [...prev, ...result.logs]);
      setAuditNextBefore(result.nextBefore);
    } catch (err) {
      setAuditError((err as Error).message);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'audit') loadAuditLogs(auditFilterUserId || undefined); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="text-center py-20 text-gray-500">Loading…</div>;
  if (!isAuthenticated || !currentUser?.roles.includes('Admin')) {
    return <Navigate to="/" replace />;
  }

  const q = userSearch.toLowerCase();
  const filteredUsers = userSearch.trim()
    ? users.filter(u =>
        u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      )
    : users;

  const auditUserResults = auditUserSearch.trim()
    ? users
        .filter(u =>
          u.syncStatus === 'ok' &&
          (u.name.toLowerCase().includes(auditUserSearch.toLowerCase()) ||
           u.email.toLowerCase().includes(auditUserSearch.toLowerCase()))
        )
        .slice(0, 8)
    : [];

  const selectAuditUser = (user: AdminUser) => {
    setAuditFilterUserId(user.userId);
    setAuditFilterUserName(user.name);
    setAuditUserSearch('');
    setShowUserDropdown(false);
    loadAuditLogs(user.userId);
  };

  const clearAuditFilter = () => {
    setAuditFilterUserId('');
    setAuditFilterUserName('');
    setAuditUserSearch('');
    loadAuditLogs(undefined);
  };

  const startEditRoles = (user: AdminUser) => {
    setEditingRoles(user.userId);
    setSelectedRoles([...user.roles]);
  };

  const toggleRole = (role: UserRole) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const saveRoles = async (userId: string) => {
    if (selectedRoles.length === 0) return;
    setSaving(userId);
    try {
      await updateUserRoles(userId, selectedRoles);
      setEditingRoles(null);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const toggleStatus = async (user: AdminUser) => {
    if (user.userId === currentUser.userId) {
      alert('You cannot disable your own account.');
      return;
    }
    const next = user.accountStatus === 'active' ? 'disabled' : 'active';
    if (!confirm(`${next === 'disabled' ? 'Disable' : 'Re-enable'} ${user.email}?`)) return;
    setSaving(user.userId);
    try {
      await updateUserStatus(user.userId, next);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const handleRepair = async (user: AdminUser) => {
    if (!confirm(`Create platform profile for ${user.email}?`)) return;
    setRepairing(user.userId);
    try {
      await repairUser(user.userId);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setRepairing(null);
    }
  };

  const handleResolve = async (requestId: string, action: 'approve' | 'deny') => {
    setResolvingReq(requestId);
    try {
      await resolvePermissionRequest(requestId, action);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setResolvingReq(null);
    }
  };

  const handleSetVerification = async (user: AdminUser, status: 'provisional' | 'verified' | 'rejected') => {
    setVerifyingUser(user.userId);
    try {
      await setTeacherVerificationStatus(user.userId, status);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setVerifyingUser(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin</h1>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {(['users', 'audit'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border border-transparent ${
              activeTab === tab
                ? 'border-gray-200 border-b-white bg-white text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'users' ? 'Users & Requests' : 'Audit Log'}
            {tab === 'users' && permRequests.length > 0 && (
              <span className="ml-1.5 text-xs bg-amber-200 text-amber-800 rounded-full px-1.5 py-0.5">
                {permRequests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'audit' && (
        <div>
          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-600 shrink-0">Filter by user:</label>
            <div className="relative">
              {auditFilterUserId ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full font-medium">
                    {auditFilterUserName}
                  </span>
                  <button
                    onClick={clearAuditFilter}
                    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                    title="Clear filter"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={auditUserSearch}
                  onChange={e => { setAuditUserSearch(e.target.value); setShowUserDropdown(true); }}
                  onFocus={() => setShowUserDropdown(true)}
                  onBlur={() => setTimeout(() => setShowUserDropdown(false), 150)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 placeholder-gray-400 w-64"
                />
              )}
              {showUserDropdown && auditUserSearch.trim() && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-52 overflow-y-auto">
                  {auditUserResults.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-400">No users found</div>
                  ) : (
                    auditUserResults.map(u => (
                      <button
                        key={u.userId}
                        onMouseDown={() => selectAuditUser(u)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <div className="font-medium text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {auditError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm mb-4">{auditError}</div>
          )}

          {auditLoading && auditLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Loading audit log…</div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No audit entries found.</div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">Time</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Admin</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">User affected</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {auditLogs.map(entry => (
                      <tr key={entry.logId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleString('en-US', {
                            month: 'short', day: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{entry.actorName}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              setAuditFilterUserId(entry.targetUserId);
                              setAuditFilterUserName(entry.targetUserName);
                              loadAuditLogs(entry.targetUserId);
                            }}
                            className="text-blue-600 hover:underline text-left"
                          >
                            {entry.targetUserName}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{auditSummary(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditNextBefore && (
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-center">
                  <button
                    onClick={loadMoreAuditLogs}
                    disabled={auditLoading}
                    className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    {auditLoading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
      <>

      {permRequests.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm mb-6 overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
            <h2 className="font-semibold text-amber-900">
              Pending Role Requests
              <span className="ml-2 text-xs bg-amber-200 text-amber-800 rounded-full px-2 py-0.5">
                {permRequests.length}
              </span>
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {permRequests.map(req => (
              <div key={req.requestId} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-gray-900 text-sm">{req.userName}</span>
                    <span className="text-xs text-gray-400">{req.userEmail}</span>
                    <span className="ml-auto text-xs text-gray-400">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {req.targetRole}
                    </span>
                    {req.yogaAllianceUrl && (
                      <a
                        href={req.yogaAllianceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Yoga Alliance profile ↗
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{req.reason}</p>
                </div>
                <div className="flex gap-2 sm:shrink-0">
                  <button
                    onClick={() => handleResolve(req.requestId, 'approve')}
                    disabled={resolvingReq === req.requestId}
                    className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleResolve(req.requestId, 'deny')}
                    disabled={resolvingReq === req.requestId}
                    className="text-xs px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm mb-6">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading users…</div>
      ) : (
        <>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name or email…"
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-72 text-gray-700 placeholder-gray-400"
          />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">User</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Roles</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Member since</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map(user => (
                user.syncStatus === 'cognito_only' ? (
                  <tr key={user.userId} className="bg-amber-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-gray-500 text-xs">{user.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-amber-700 italic">No platform profile</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        unsynced
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleRepair(user)}
                          disabled={repairing === user.userId}
                          className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                        >
                          {repairing === user.userId ? 'Repairing…' : 'Repair'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                <tr key={user.userId} className={user.accountStatus === 'disabled' ? 'opacity-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-gray-500 text-xs">{user.email}</div>
                  </td>

                  <td className="px-4 py-3">
                    {editingRoles === user.userId ? (
                      <div className="space-y-1">
                        {ALL_ROLES.map(role => (
                          <label key={role} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedRoles.includes(role)}
                              onChange={() => toggleRole(role)}
                              className="rounded text-green-600"
                            />
                            <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[role]}`}>{role}</span>
                          </label>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => saveRoles(user.userId)}
                            disabled={saving === user.userId || selectedRoles.length === 0}
                            className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {saving === user.userId ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingRoles(null)}
                            className="text-xs px-2 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map(role => (
                          <span key={role} className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[role]}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${
                        user.accountStatus === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {user.accountStatus}
                      </span>
                      {user.verificationStatus && (
                        <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${VERIFICATION_COLORS[user.verificationStatus]}`}>
                          {user.verificationStatus}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5 items-end">
                      <div className="flex gap-2">
                        {editingRoles !== user.userId && (
                          <button
                            onClick={() => startEditRoles(user)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Edit roles
                          </button>
                        )}
                        {user.userId !== currentUser.userId && (
                          <button
                            onClick={() => toggleStatus(user)}
                            disabled={saving === user.userId}
                            className={`text-xs disabled:opacity-50 ${
                              user.accountStatus === 'active'
                                ? 'text-red-500 hover:text-red-700'
                                : 'text-green-600 hover:text-green-800'
                            }`}
                          >
                            {saving === user.userId ? '…' : user.accountStatus === 'active' ? 'Disable' : 'Enable'}
                          </button>
                        )}
                      </div>
                      {user.verificationStatus !== undefined && (
                        <select
                          disabled={verifyingUser === user.userId}
                          value=""
                          onChange={e => {
                            const v = e.target.value as 'provisional' | 'verified' | 'rejected';
                            if (v) handleSetVerification(user, v);
                          }}
                          className="text-xs border border-gray-300 rounded px-1.5 py-0.5 text-gray-600 disabled:opacity-50"
                        >
                          <option value="" disabled>Set verification…</option>
                          <option value="provisional">Provisional</option>
                          <option value="verified">Verified</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
                )
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {userSearch.trim() && filteredUsers.length !== users.length
              ? `${filteredUsers.length} of ${users.length} users`
              : `${users.length} user${users.length !== 1 ? 's' : ''}`
            }
          </div>
          </div>
        </div>
        </>
      )}
      </>
      )}
    </div>
  );
};
